import axios from 'axios';
import { env } from '@/lib/env';

/**
 * Base URL for SAP job owner mappings (owner = application component text -> email / ServiceNow group) (authapi, administrators only).
 * Gateway: BACKEND_URL ends with /backend -> /backend/api/settings/job-owner-mappings.
 */
export function getJobOwnerMappingsApiBase() {
  const backend = (env('REACT_APP_BACKEND_URL') || '').replace(/\/$/, '');
  if (!backend) return '';
  if (backend.endsWith('/backend')) return `${backend}/api/settings/job-owner-mappings`;
  if (backend.endsWith('/api')) return `${backend}/settings/job-owner-mappings`;
  return `${backend}/api/settings/job-owner-mappings`;
}

const BASE = getJobOwnerMappingsApiBase();

const EMAIL_RE =
  /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/;

export const MAX_EMAILS = 10;

/** "a@x.io; b@y.io,\nc@z.io" -> ['a@x.io', 'b@y.io', 'c@z.io'] */
export function splitEmails(raw) {
  return String(raw ?? '')
    .split(/[,;\n]+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

export function isValidEmail(value) {
  const text = String(value ?? '').trim();
  return text.length <= 254 && text.split('@')[0].length <= 64 && EMAIL_RE.test(text);
}

/** Message for the email field, or '' when it is empty or valid. */
export function emailFieldError(raw) {
  const parts = splitEmails(raw);
  if (!parts.length) return '';
  const invalid = parts.filter((p) => !isValidEmail(p));
  if (invalid.length) {
    return `Not a valid email address: ${invalid.join(', ')}`;
  }
  if (new Set(parts.map((p) => p.toLowerCase())).size > MAX_EMAILS) {
    return `At most ${MAX_EMAILS} email addresses are allowed`;
  }
  return '';
}

export const EMPTY_MAPPING_FORM = {
  owner: '',
  is_default: false,
  email: '',
  assignment_group: '',
  notes: '',
  enabled: true,
};

export function formFromMapping(mapping) {
  return {
    owner: mapping.owner || '',
    is_default: Boolean(mapping.is_default),
    email: mapping.email || '',
    assignment_group: mapping.assignment_group || '',
    notes: mapping.notes || '',
    enabled: mapping.enabled !== false,
  };
}

/**
 * Field errors for the form. Empty object means it can be saved.
 * `contact` is the shared "email or assignment group" rule.
 */
export function validateMappingForm(form) {
  const errors = {};
  if (!form.is_default && !String(form.owner || '').trim()) {
    errors.owner = 'Enter the owner (the SAP application component text).';
  }
  const emailError = emailFieldError(form.email);
  if (emailError) errors.email = emailError;
  const hasEmail = splitEmails(form.email).length > 0;
  const hasGroup = Boolean(String(form.assignment_group || '').trim());
  if (!hasEmail && !hasGroup) {
    errors.contact = 'Provide an email, an assignment group, or both.';
  }
  return errors;
}

/** Request body from the form. The server validates again. */
export function payloadFromForm(form) {
  return {
    owner: form.is_default ? '' : String(form.owner || '').trim(),
    is_default: Boolean(form.is_default),
    email: String(form.email || '').trim() || null,
    assignment_group: String(form.assignment_group || '').trim() || null,
    notes: String(form.notes || '').trim() || null,
    enabled: Boolean(form.enabled),
  };
}

export function formatOwnerMappingError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (status === 403) return 'Only administrators can view or change job owners.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => (typeof item === 'object' && item?.msg ? item.msg : String(item)))
      .join('; ');
  }
  return err?.message || 'Request failed';
}

export async function fetchJobOwnerMappings(params = {}) {
  const { data } = await axios.get(BASE, { params });
  return data;
}

export async function createJobOwnerMapping(body) {
  const { data } = await axios.post(BASE, body);
  return data;
}

export async function updateJobOwnerMapping(id, body) {
  const { data } = await axios.put(`${BASE}/${encodeURIComponent(id)}`, body);
  return data;
}

export async function deleteJobOwnerMapping(id) {
  const { data } = await axios.delete(`${BASE}/${encodeURIComponent(id)}`);
  return data;
}

/** ServiceNow assignment groups; only these may be saved on a mapping. */
export async function fetchAssignmentGroups({ refresh = false } = {}) {
  const { data } = await axios.get(`${BASE}/assignment-groups`, {
    params: refresh ? { refresh: true } : {},
  });
  return data;
}
